import { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded'
import DragIndicatorRoundedIcon from '@mui/icons-material/DragIndicatorRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import {
  adminService,
  type AdminBlock,
  type AdminEmailTemplate,
  type AttendeeRow,
  type BlockWrite,
} from '../../services/adminService'
import { LoadingState } from '../../components/ui/States'
import { apiError } from '../errors'
import { useToast } from '../useToast'
import { confirmDelete } from '../confirm'
import BlockEditor from '../components/BlockEditor'

// The transactional emails that can be overridden per event.
const EMAIL_KINDS: { value: string; label: string }[] = [
  { value: 'registration_invite', label: 'Registration invite' },
  { value: 'registration_confirmation', label: 'Registration confirmation' },
  { value: 'selection_confirmation', label: 'Dinner/selection confirmation' },
  { value: 'magic_link', label: 'Sign-in (magic link)' },
  { value: 'guest_travel', label: 'Travel details (special guest)' },
]

const TYPE_LABELS: Record<string, string> = {
  hero: 'Hero',
  richtext: 'Rich text',
  html: 'Custom HTML',
  image: 'Image',
  button: 'Button',
  divider: 'Divider',
  columns: 'Columns',
  travel: 'Travel details',
}

export default function EmailComposerManager({ eventId }: { eventId: string }) {
  const { notify } = useToast()
  const [templates, setTemplates] = useState<AdminEmailTemplate[]>([])
  const [loading, setLoading] = useState(true)
  const [newKind, setNewKind] = useState(EMAIL_KINDS[0].value)

  const load = () =>
    adminService
      .listEmailTemplates(eventId)
      .then(setTemplates)
      .catch((e) => notify(apiError(e), 'error'))
      .finally(() => setLoading(false))

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId])

  const existingKinds = new Set(templates.map((t) => t.kind))
  const available = EMAIL_KINDS.filter((k) => !existingKinds.has(k.value))

  const create = async () => {
    try {
      await adminService.createEmailTemplate(eventId, newKind)
      notify('Custom email created')
      load()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  if (loading) return <LoadingState />

  return (
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Override the built-in transactional emails with your own composed
        content. Blocks use your event branding. Add a <strong>Button / CTA</strong>{' '}
        block and set its <strong>Action</strong> to <em>Register</em> or{' '}
        <em>Sign in</em> to drop in the right link automatically, or use{' '}
        <code>{'{{link}}'}</code> for a personal magic link. Use{' '}
        <strong>Preview</strong> to see the rendered email. Available tokens:{' '}
        <code>{'{{event_name}}'}</code>, <code>{'{{link}}'}</code>,{' '}
        <code>{'{{attendee_name}}'}</code>. The{' '}
        <strong>Travel details</strong> email also supports a{' '}
        <strong>Travel details</strong> block (renders the guest&apos;s flights,
        hotel &amp; traveler numbers) and <code>{'{{travel_*}}'}</code> tokens
        (e.g. <code>{'{{travel_hotel_name}}'}</code>).
      </Typography>

      {available.length > 0 && (
        <Stack direction="row" spacing={1.5} sx={{ mb: 3 }}>
          <TextField
            select
            size="small"
            label="Email"
            value={newKind}
            onChange={(e) => setNewKind(e.target.value)}
            sx={{ minWidth: 260 }}
          >
            {available.map((k) => (
              <MenuItem key={k.value} value={k.value}>
                {k.label}
              </MenuItem>
            ))}
          </TextField>
          <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={create}>
            Customize
          </Button>
        </Stack>
      )}

      <Stack spacing={2}>
        {templates.map((t) => (
          <EmailTemplateCard
            key={t.id}
            eventId={eventId}
            template={t}
            onChanged={load}
          />
        ))}
      </Stack>
    </Box>
  )
}

function EmailTemplateCard({
  eventId,
  template,
  onChanged,
}: {
  eventId: string
  template: AdminEmailTemplate
  onChanged: () => void
}) {
  const { notify } = useToast()
  const [subject, setSubject] = useState(template.subject ?? '')
  const [enabled, setEnabled] = useState(template.enabled)
  const [editing, setEditing] = useState<
    { block: AdminBlock | null; parentId?: string; column?: number } | null
  >(null)
  // Local copy of the blocks for optimistic drag-to-reorder; kept in sync when
  // the parent reloads (add/delete). Backend returns blocks ordered by ordinal.
  const [blocks, setBlocks] = useState<AdminBlock[]>(template.blocks)
  useEffect(() => {
    setBlocks(template.blocks)
  }, [template.blocks])
  const label =
    EMAIL_KINDS.find((k) => k.value === template.kind)?.label ?? template.kind

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  )

  const onDragEnd = async (e: DragEndEvent) => {
    if (!e.over || e.active.id === e.over.id) return
    const ids = blocks.map((b) => b.id)
    const from = ids.indexOf(String(e.active.id))
    const to = ids.indexOf(String(e.over.id))
    if (from < 0 || to < 0) return
    const next = arrayMove(blocks, from, to)
    setBlocks(next) // optimistic
    try {
      await adminService.reorderEmailBlocks(
        eventId,
        template.id,
        next.map((b) => b.id),
      )
    } catch (err) {
      notify(apiError(err), 'error')
      onChanged() // reload to restore server order
    }
  }

  const saveMeta = async (next: { subject?: string; enabled?: boolean }) => {
    try {
      await adminService.updateEmailTemplate(eventId, template.id, next)
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const saveBlock = async (payload: BlockWrite) => {
    try {
      if (editing?.block) {
        await adminService.updateBlock(eventId, editing.block.id, payload)
      } else {
        await adminService.createEmailBlock(eventId, template.id, {
          ...payload,
          parentBlockId: editing?.parentId ?? null,
          config: {
            ...(payload.config ?? {}),
            ...(editing?.column != null ? { column: editing.column } : {}),
          },
        })
      }
      setEditing(null)
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const removeBlock = async (b: AdminBlock) => {
    if (!(await confirmDelete('Delete this block?'))) return
    try {
      await adminService.deleteBlock(eventId, b.id)
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const remove = async () => {
    if (!(await confirmDelete(`Remove the custom "${label}" email?`))) return
    try {
      await adminService.deleteEmailTemplate(eventId, template.id)
      onChanged()
    } catch (e) {
      notify(apiError(e), 'error')
    }
  }

  const [sending, setSending] = useState(false)
  const testSend = async () => {
    const to = window.prompt('Send a test to which email address?')
    if (!to) return
    setSending(true)
    try {
      await adminService.testSendEmailTemplate(eventId, template.id, to.trim())
      notify(`Test email sent to ${to.trim()}`)
    } catch (e) {
      notify(apiError(e), 'error')
    } finally {
      setSending(false)
    }
  }

  const [previewOpen, setPreviewOpen] = useState(false)

  return (
    <Card variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <Typography variant="subtitle1" fontWeight={700} sx={{ flex: 1 }}>
          {label}
        </Typography>
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={(e) => {
                setEnabled(e.target.checked)
                saveMeta({ enabled: e.target.checked })
              }}
            />
          }
          label="Enabled"
        />
        <Button
          size="small"
          startIcon={<VisibilityRoundedIcon />}
          onClick={() => setPreviewOpen(true)}
          disabled={template.blocks.length === 0}
        >
          Preview
        </Button>
        <Button
          size="small"
          startIcon={<SendRoundedIcon />}
          onClick={testSend}
          disabled={sending || !enabled || template.blocks.length === 0}
        >
          {sending ? 'Sending…' : 'Send test'}
        </Button>
        <IconButton size="small" color="error" onClick={remove}>
          <DeleteRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>

      <TextField
        label="Subject"
        fullWidth
        size="small"
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        onBlur={() => saveMeta({ subject })}
        sx={{ mb: 2 }}
      />

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
        <SortableContext items={blocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
          <Stack spacing={1}>
            {blocks.map((b) => (
              <SortableEmailBlock
                key={b.id}
                block={b}
                onEdit={() => setEditing({ block: b })}
                onDelete={() => removeBlock(b)}
                onEditChild={(child) => setEditing({ block: child })}
                onDeleteChild={(child) => removeBlock(child)}
                onAddChild={(col) => setEditing({ block: null, parentId: b.id, column: col })}
              />
            ))}
          </Stack>
        </SortableContext>
      </DndContext>

      <Button size="small" startIcon={<AddRoundedIcon />} sx={{ mt: 1 }} onClick={() => setEditing({ block: null })}>
        Add block
      </Button>

      {editing && (
        <BlockEditor
          eventId={eventId}
          block={editing.block}
          onClose={() => setEditing(null)}
          onSave={saveBlock}
        />
      )}

      {previewOpen && (
        <EmailPreviewDialog
          eventId={eventId}
          templateId={template.id}
          label={label}
          onClose={() => setPreviewOpen(false)}
        />
      )}
    </Card>
  )
}

const blockSummary = (b: AdminBlock): string =>
  (b.config?.title as string) ||
  (b.config?.label as string) ||
  (b.source?.slice(0, 40) ?? '')

/**
 * A draggable email block row. Columns blocks expand to show each column with
 * its own child blocks and an "Add" control — matching the page builder, so
 * content can be placed inside email columns.
 */
function SortableEmailBlock({
  block,
  onEdit,
  onDelete,
  onEditChild,
  onDeleteChild,
  onAddChild,
}: {
  block: AdminBlock
  onEdit: () => void
  onDelete: () => void
  onEditChild: (child: AdminBlock) => void
  onDeleteChild: (child: AdminBlock) => void
  onAddChild: (column: number) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: block.id })
  const isColumns = block.blockType === 'columns'
  const colCount = Number(block.config?.columnCount ?? 2)
  return (
    <Card
      ref={setNodeRef}
      variant="outlined"
      style={{ transform: CSS.Transform.toString(transform), transition }}
      sx={{ p: 1, borderRadius: 2, opacity: isDragging ? 0.6 : 1 }}
    >
      <Stack direction="row" alignItems="center" gap={1}>
        <IconButton size="small" sx={{ cursor: 'grab' }} {...attributes} {...listeners}>
          <DragIndicatorRoundedIcon fontSize="small" />
        </IconButton>
        <Chip
          label={TYPE_LABELS[block.blockType] || block.blockType}
          size="small"
          sx={{ height: 22, fontWeight: 700 }}
        />
        <Typography variant="body2" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
          {blockSummary(block)}
        </Typography>
        <IconButton size="small" onClick={onEdit} title="Edit">
          <EditRoundedIcon fontSize="small" />
        </IconButton>
        <IconButton size="small" color="error" onClick={onDelete} title="Delete">
          <DeleteRoundedIcon fontSize="small" />
        </IconButton>
      </Stack>

      {isColumns && (
        <Grid container spacing={1} sx={{ mt: 0.5 }}>
          {Array.from({ length: colCount }).map((_, col) => {
            const kids = (block.children ?? []).filter(
              (c) => ((c.config?.column as number) ?? 0) === col,
            )
            return (
              <Grid item xs={12} md={12 / colCount} key={col}>
                <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                  <Typography variant="caption" color="text.secondary">
                    Column {col + 1}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {kids.map((c) => (
                      <Stack key={c.id} direction="row" alignItems="center" gap={0.5}>
                        <Chip label={TYPE_LABELS[c.blockType] || c.blockType} size="small" sx={{ height: 20 }} />
                        <Typography variant="caption" noWrap sx={{ flex: 1, minWidth: 0 }}>
                          {blockSummary(c)}
                        </Typography>
                        <IconButton size="small" onClick={() => onEditChild(c)}>
                          <EditRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => onDeleteChild(c)}>
                          <DeleteRoundedIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Stack>
                    ))}
                    <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => onAddChild(col)}>
                      Add
                    </Button>
                  </Stack>
                </Box>
              </Grid>
            )
          })}
        </Grid>
      )}
    </Card>
  )
}

/**
 * Renders composed email HTML in an isolated iframe (email HTML must not inherit
 * the admin app's styles, and iframe isolation is the safe way to show it).
 *
 * Includes a "Preview as" attendee picker: choose a real attendee to see the
 * personalization tokens (name/email) as they'd receive them, or the generic
 * sample. Re-fetches the rendered HTML whenever the selection changes.
 */
function EmailPreviewDialog({
  eventId,
  templateId,
  label,
  onClose,
}: {
  eventId: string
  templateId: string
  label: string
  onClose: () => void
}) {
  const { notify } = useToast()
  const [attendees, setAttendees] = useState<AttendeeRow[]>([])
  const [selected, setSelected] = useState<string>('') // '' = sample
  const [data, setData] = useState<{ subject: string; html: string } | null>(null)
  const [loading, setLoading] = useState(true)

  // Load the attendee list once for the picker (best-effort; picker still works
  // with just the sample option if this fails).
  useEffect(() => {
    adminService
      .listAttendees(eventId)
      .then(setAttendees)
      .catch(() => setAttendees([]))
  }, [eventId])

  // (Re)render the preview whenever the selected attendee changes.
  useEffect(() => {
    let active = true
    setLoading(true)
    adminService
      .previewEmailTemplate(eventId, templateId, selected || undefined)
      .then((d) => {
        if (active) setData(d)
      })
      .catch((e) => {
        if (active) notify(apiError(e), 'error')
      })
      .finally(() => {
        if (active) setLoading(false)
      })
    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, templateId, selected])

  const attendeeLabel = (a: AttendeeRow) =>
    a.fullName ? `${a.fullName} (${a.email})` : a.email

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Preview — {label}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          select
          size="small"
          fullWidth
          label="Preview as"
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
          sx={{ mb: 2 }}
        >
          <MenuItem value="">Sample attendee</MenuItem>
          {attendees.map((a) => (
            <MenuItem key={a.id} value={a.id}>
              {attendeeLabel(a)}
            </MenuItem>
          ))}
        </TextField>

        <Typography variant="caption" color="text.secondary">
          Subject
        </Typography>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 700 }}>
          {data?.subject ?? '…'}
        </Typography>
        <Box sx={{ position: 'relative' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255,255,255,0.6)',
                zIndex: 1,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          )}
          <Box
            component="iframe"
            title="Email preview"
            srcDoc={data?.html ?? ''}
            sx={{
              width: '100%',
              height: 520,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              bgcolor: '#fff',
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
          {selected
            ? 'Personalization (name/email) reflects the selected attendee. '
            : 'Sample values are used for personalization tokens. '}
          <code>{'{{link}}'}</code> always shows a sample sign-in link (no real
          token is issued for a preview).
        </Typography>
      </DialogContent>
    </Dialog>
  )
}
